import { CheckOutlined } from '@ant-design/icons';
import { Button, Flex, Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import {
  buildRateFeedbackFormSchema,
  RATE_FEEDBACK_TAG_KEYS,
} from '@/utils/validation/formSchemas/miscSchemas';
import { validateSchemaForm } from '@/utils/validation/validateSchemaForm';

import type { SchemaSection } from '@/components/form/SchemaForm/types';

type RateFormValues = Record<string, boolean | string> & {
  comments: string;
};

export type { RateFormValues };

interface RateFormProps {
  onFinish: (data: RateFormValues) => void;
  loading: boolean;
}

const GOOD_KEYS = [
  'nice_to_work',
  'kept_agreements',
  'good_attention',
] as const satisfies ReadonlyArray<(typeof RATE_FEEDBACK_TAG_KEYS)[number]>;
const BAD_KEYS = [
  'need_better_communication',
  'deadlines_missed',
  'different_role',
  'not_enough_attention',
] as const satisfies ReadonlyArray<(typeof RATE_FEEDBACK_TAG_KEYS)[number]>;

const INITIAL_TAG_VALUES = Object.fromEntries(
  RATE_FEEDBACK_TAG_KEYS.map((key) => [key, false]),
) as Record<(typeof RATE_FEEDBACK_TAG_KEYS)[number], boolean>;

const RateForm = ({ onFinish, loading }: RateFormProps) => {
  const [form] = Form.useForm();
  const [t] = useTranslation();
  const formSchema = useMemo(() => buildRateFeedbackFormSchema(t), [t]);
  const hydratedDefaultsRef = useRef(false);

  useEffect(() => {
    if (hydratedDefaultsRef.current) return;
    hydratedDefaultsRef.current = true;
    form.setFieldsValue({
      ...INITIAL_TAG_VALUES,
      comments: '',
    });
  }, [form]);

  const toggleTag = useCallback(
    (key: (typeof RATE_FEEDBACK_TAG_KEYS)[number]) => {
      const currentValue = Boolean(form.getFieldValue(key));
      form.setFieldValue(key, !currentValue);
    },
    [form],
  );

  const tagValues = Form.useWatch([], form) as
    | Partial<Record<(typeof RATE_FEEDBACK_TAG_KEYS)[number], boolean>>
    | undefined;

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'feedback-tags',
        fields: [
          {
            key: 'good-tags',
            type: 'custom',
            render: () => (
              <Flex vertical>
                {GOOD_KEYS.map((el) => {
                  const isActive = Boolean(tagValues?.[el]);
                  return (
                    <Button
                      key={el}
                      block
                      variant={isActive ? 'solid' : 'outlined'}
                      onClick={() => toggleTag(el)}
                    >
                      <Flex justify="space-between" align="center">
                        {t(el.toUpperCase())}
                        {isActive ? <CheckOutlined /> : null}
                      </Flex>
                    </Button>
                  );
                })}
                {BAD_KEYS.map((el) => {
                  const isActive = Boolean(tagValues?.[el]);
                  return (
                    <Button
                      key={el}
                      block
                      color="default"
                      variant={isActive ? 'solid' : 'outlined'}
                      onClick={() => toggleTag(el)}
                    >
                      <Flex justify="space-between" align="center">
                        {t(el.toUpperCase())}
                        {isActive ? <CheckOutlined /> : null}
                      </Flex>
                    </Button>
                  );
                })}
              </Flex>
            ),
          },
          {
            name: 'comments',
            label: t('COMMENT'),
            type: 'textarea',
            componentProps: { autoSize: { minRows: 3 } },
          },
        ],
      },
    ],
    [t, tagValues, toggleTag],
  );

  const handleSubmit = useCallback(() => {
    const values = form.getFieldsValue(true) as Record<string, unknown>;
    const validation = validateSchemaForm(form, formSchema, values);
    if (!validation.ok) {
      return;
    }
    onFinish(validation.data as RateFormValues);
  }, [form, formSchema, onFinish]);

  return (
    <SchemaForm
      form={form}
      formSchema={formSchema}
      layout="vertical"
      sections={sections}
      footer={{
        actions: [
          {
            label: t('LEAVE_FEEDBACK_RATE'),
            type: 'primary',
            loading,
            onClick: handleSubmit,
          },
        ],
      }}
    />
  );
};

export default RateForm;
